import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const STATUS_COLORS = {
  OPEN:        { bg: '#dbeafe', text: '#1e40af' },
  IN_PROGRESS: { bg: '#fef3c7', text: '#92400e' },
  RESOLVED:    { bg: '#d1fae5', text: '#065f46' },
  VERIFIED:    { bg: '#dcfce7', text: '#14532d' },
  CLOSED:      { bg: '#f1f5f9', text: '#64748b' },
  WONT_FIX:   { bg: '#f3f4f6', text: '#6b7280' },
};

const SEV_DOT = {
  CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#ca8a04', LOW: '#0284c7',
};


function TicketPanel({ project, onBack, orgId }) {
  const { t } = useTranslation();
  const [tickets, setTickets]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [msg, setMsg]             = useState('');
  const [showAdd, setShowAdd]     = useState(false);
  const [newTitle, setNewTitle]   = useState('');
  const [newSev, setNewSev]       = useState('MEDIUM');
  const [newDue, setNewDue]       = useState('');
  const [saving, setSaving]       = useState(false);

  const fetchTickets = useCallback(() => {
    setLoading(true);
    axiosInstance.get(`/remediation/projects/${project.id}/tickets`)
      .then(r => setTickets(r.data || []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleStatusChange = async (ticketId, status) => {
    try {
      await axiosInstance.put(`/remediation/tickets/${ticketId}/status`, { status });
      fetchTickets();
    } catch { setMsg(t('RemediationDashboard.failedUpdateTicketStatus')); }
  };

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    setSaving(true); setMsg('');
    try {
      await axiosInstance.post(`/remediation/projects/${project.id}/tickets`, {
        title: newTitle.trim(),
        severity: newSev,
        dueDate: newDue || null,
      });
      setNewTitle(''); setNewSev('MEDIUM'); setNewDue('');
      setShowAdd(false);
      fetchTickets();
    } catch { setMsg(t('RemediationDashboard.failedCreateTicket')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('RemediationDashboard.confirmDeleteTicket'))) return;
    try {
      await axiosInstance.delete(`/remediation/tickets/${id}`);
      fetchTickets();
    } catch { setMsg(t('RemediationDashboard.failedDeleteTicket')); }
  };

  const open = tickets.filter(t => t.status === 'OPEN').length;
  const inProg = tickets.filter(t => t.status === 'IN_PROGRESS').length;
  const done = tickets.filter(t => ['RESOLVED', 'VERIFIED'].includes(t.status)).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="mp-btn-secondary" onClick={onBack}>
          <i className="fas fa-arrow-left" /> {t('RemediationDashboard.back')}
        </button>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>{project.name}</h3>
          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{project.orgName}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 16 }}>
          {[[t('RemediationDashboard.statusOpen'), open, '#1e40af'], [t('RemediationDashboard.statusInProgress'), inProg, '#92400e'], [t('RemediationDashboard.statusDone'), done, '#065f46']].map(([l, n, c]) => (
            <div key={l} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: c }}>{n}</div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {msg && <div className="mp-msg mp-msg-err" style={{ marginBottom: 10 }}>{msg}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="mp-btn-primary" onClick={() => setShowAdd(!showAdd)}>
          <i className="fas fa-plus" /> {t('RemediationDashboard.addTicket')}
        </button>
      </div>

      {showAdd && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="mp-field" style={{ flex: 2, minWidth: 200, margin: 0 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{t('RemediationDashboard.titleLabel')}</label>
            <input className="mp-input" value={newTitle} onChange={e => setNewTitle(e.target.value)}
              placeholder={t('RemediationDashboard.titlePlaceholder')} autoFocus />
          </div>
          <div className="mp-field" style={{ flex: 0, minWidth: 110, margin: 0 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{t('RemediationDashboard.severity')}</label>
            <select className="mp-input" value={newSev} onChange={e => setNewSev(e.target.value)}>
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="mp-field" style={{ flex: 0, minWidth: 140, margin: 0 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{t('RemediationDashboard.dueDate')}</label>
            <input type="date" className="mp-input" value={newDue} onChange={e => setNewDue(e.target.value)} />
          </div>
          <button className="mp-btn-primary" onClick={handleAdd} disabled={saving || !newTitle.trim()}>
            {saving ? <i className="fas fa-spinner fa-spin" /> : t('RemediationDashboard.save')}
          </button>
          <button className="mp-btn-secondary" onClick={() => setShowAdd(false)}>{t('RemediationDashboard.cancel')}</button>
        </div>
      )}

      {loading ? (
        <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('RemediationDashboard.loadingTickets')}</div>
      ) : tickets.length === 0 ? (
        <div className="mp-empty">{t('RemediationDashboard.noTickets')}</div>
      ) : (
        <table className="mp-table">
          <thead>
            <tr><th>{t('RemediationDashboard.colTitle')}</th><th>{t('RemediationDashboard.colSeverity')}</th><th>{t('RemediationDashboard.colStatus')}</th><th>{t('RemediationDashboard.colAssignee')}</th><th>{t('RemediationDashboard.colDueDate')}</th><th>{t('RemediationDashboard.colResolved')}</th><th></th></tr>
          </thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t.id}>
                <td>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{t.title}</div>
                  {t.originatingFindingType && (
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                      {t.originatingFindingType}#{t.originatingFindingId}
                    </div>
                  )}
                </td>
                <td>
                  {t.severity && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: SEV_DOT[t.severity] || '#64748b' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: SEV_DOT[t.severity] || '#94a3b8' }} />
                      {t.severity}
                    </span>
                  )}
                </td>
                <td>
                  <select value={t.status}
                    onChange={e => handleStatusChange(t.id, e.target.value)}
                    style={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6,
                      padding: '2px 6px', background: '#fff', cursor: 'pointer' }}>
                    {['OPEN', 'IN_PROGRESS', 'RESOLVED', 'VERIFIED', 'WONT_FIX'].map(s => (
                      <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </td>
                <td style={{ fontSize: '0.8rem', color: '#475569' }}>{t.assigneeName || '—'}</td>
                <td style={{ fontSize: '0.78rem', color: t.dueDate && new Date(t.dueDate) < new Date() ? '#dc2626' : '#64748b' }}>
                  {t.dueDate ? new Date(t.dueDate).toLocaleDateString() : '—'}
                </td>
                <td style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                  {t.resolvedAt ? new Date(t.resolvedAt).toLocaleDateString() : '—'}
                </td>
                <td>
                  <button className="mp-btn-sm mp-btn-sm-outline" title={t('RemediationDashboard.delete')}
                    onClick={() => handleDelete(t.id)}
                    style={{ color: '#ef4444', borderColor: '#fca5a5' }}>
                    <i className="fas fa-trash-alt" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function RemediationDashboard({ orgId }) {
  const { t } = useTranslation();
  const [projects, setProjects]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [msg, setMsg]                 = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAdd, setShowAdd]         = useState(false);
  const [newName, setNewName]         = useState('');
  const [newDesc, setNewDesc]         = useState('');
  const [saving, setSaving]           = useState(false);

  const fetchProjects = useCallback(() => {
    setLoading(true);
    const url = orgId ? `/remediation/projects/org/${orgId}` : '/remediation/projects';
    axiosInstance.get(url)
      .then(r => setProjects(r.data || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleCreateProject = async () => {
    if (!newName.trim()) return;
    setSaving(true); setMsg('');
    try {
      const url = orgId ? `/remediation/projects/org/${orgId}` : '/remediation/projects';
      await axiosInstance.post(url, { name: newName.trim(), description: newDesc });
      setNewName(''); setNewDesc(''); setShowAdd(false);
      fetchProjects();
    } catch { setMsg(t('RemediationDashboard.failedCreateProject')); }
    finally { setSaving(false); }
  };

  const handleCloseProject = async (projectId) => {
    if (!window.confirm(t('RemediationDashboard.confirmCloseProject'))) return;
    try {
      await axiosInstance.put(`/remediation/projects/${projectId}/status`, { status: 'CLOSED' });
      fetchProjects();
    } catch { setMsg(t('RemediationDashboard.failedCloseProject')); }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm(t('RemediationDashboard.confirmDeleteProject'))) return;
    try {
      await axiosInstance.delete(`/remediation/projects/${projectId}`);
      fetchProjects();
    } catch { setMsg(t('RemediationDashboard.failedDeleteProject')); }
  };

  if (selectedProject) {
    return (
      <TicketPanel
        project={selectedProject}
        orgId={orgId}
        onBack={() => setSelectedProject(null)}
      />
    );
  }

  const totalOpen = projects.reduce((s, p) => s + (p.ticketCounts?.open || 0), 0);
  const totalInProg = projects.reduce((s, p) => s + (p.ticketCounts?.inProgress || 0), 0);
  const totalDone = projects.reduce((s, p) => s + (p.ticketCounts?.resolved || 0), 0);

  return (
    <div>
      {/* Summary */}
      <div className="mp-kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('RemediationDashboard.kpiProjects')}</div>
          <div className="mp-kpi-value">{projects.length}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('RemediationDashboard.kpiOpenTickets')}</div>
          <div className="mp-kpi-value" style={{ color: totalOpen > 0 ? '#1e40af' : '#94a3b8' }}>{totalOpen}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('RemediationDashboard.kpiInProgress')}</div>
          <div className="mp-kpi-value" style={{ color: totalInProg > 0 ? '#92400e' : '#94a3b8' }}>{totalInProg}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('RemediationDashboard.kpiResolved')}</div>
          <div className="mp-kpi-value" style={{ color: '#065f46' }}>{totalDone}</div>
        </div>
      </div>

      {msg && <div className="mp-msg mp-msg-err" style={{ marginBottom: 10 }}>{msg}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="mp-btn-primary" onClick={() => setShowAdd(!showAdd)}>
          <i className="fas fa-folder-plus" /> {t('RemediationDashboard.newProject')}
        </button>
      </div>

      {showAdd && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: '14px 16px', marginBottom: 18, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="mp-field" style={{ flex: 2, minWidth: 220, margin: 0 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{t('RemediationDashboard.projectNameLabel')}</label>
            <input className="mp-input" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder={t('RemediationDashboard.projectNamePlaceholder')} autoFocus />
          </div>
          <div className="mp-field" style={{ flex: 3, minWidth: 200, margin: 0 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{t('RemediationDashboard.description')}</label>
            <input className="mp-input" value={newDesc} onChange={e => setNewDesc(e.target.value)}
              placeholder={t('RemediationDashboard.descriptionPlaceholder')} />
          </div>
          <button className="mp-btn-primary" onClick={handleCreateProject} disabled={saving || !newName.trim()}>
            {saving ? <i className="fas fa-spinner fa-spin" /> : t('RemediationDashboard.create')}
          </button>
          <button className="mp-btn-secondary" onClick={() => setShowAdd(false)}>{t('RemediationDashboard.cancel')}</button>
        </div>
      )}

      {loading ? (
        <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('RemediationDashboard.loadingProjects')}</div>
      ) : projects.length === 0 ? (
        <div className="mp-empty">
          <i className="fas fa-clipboard-list" style={{ fontSize: 32, display: 'block', marginBottom: 8, color: '#94a3b8' }} />
          {t('RemediationDashboard.noProjects')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {projects.map(p => {
            const counts = p.ticketCounts || {};
            const total = (counts.open || 0) + (counts.inProgress || 0) + (counts.resolved || 0);
            const pct = total > 0 ? Math.round(((counts.resolved || 0) / total) * 100) : 0;
            const statusC = STATUS_COLORS[p.status] || STATUS_COLORS.OPEN;
            return (
              <div key={p.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px',
                display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }}
                onClick={() => setSelectedProject(p)}>
                <div style={{ flex: 3, minWidth: 160 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>{p.name}</div>
                  {p.orgName && (
                    <div style={{ fontSize: '0.75rem', color: '#4c75af', fontWeight: 600, marginTop: 2 }}>{p.orgName}</div>
                  )}
                  {p.description && (
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 3, whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>{p.description}</div>
                  )}
                </div>

                {/* Progress bar */}
                <div style={{ flex: 2, minWidth: 120 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#94a3b8', marginBottom: 4 }}>
                    <span>{t('RemediationDashboard.pctResolved', { pct })}</span><span>{t('RemediationDashboard.totalTickets', { count: total })}</span>
                  </div>
                  <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#16a34a' : '#3b82f6',
                      borderRadius: 3, transition: 'width 0.4s' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: '0.72rem' }}>
                    <span style={{ color: '#1e40af' }}>{t('RemediationDashboard.countOpen', { count: counts.open || 0 })}</span>
                    <span style={{ color: '#92400e' }}>{t('RemediationDashboard.countInProgress', { count: counts.inProgress || 0 })}</span>
                    <span style={{ color: '#065f46' }}>{t('RemediationDashboard.countDone', { count: counts.resolved || 0 })}</span>
                  </div>
                </div>

                <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                  background: statusC.bg, color: statusC.text, whiteSpace: 'nowrap' }}>{p.status}</span>

                {p.startedAt && (
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {new Date(p.startedAt).toLocaleDateString()}
                  </div>
                )}

                {/* Actions — stop propagation so row click doesn't fire */}
                <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                  {p.status === 'OPEN' && (
                    <button className="mp-btn-sm mp-btn-sm-outline" title={t('RemediationDashboard.closeProject')}
                      onClick={() => handleCloseProject(p.id)}>
                      <i className="fas fa-lock" />
                    </button>
                  )}
                  <button className="mp-btn-sm mp-btn-sm-outline" title={t('RemediationDashboard.deleteProject')}
                    style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                    onClick={() => handleDeleteProject(p.id)}>
                    <i className="fas fa-trash-alt" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
